import { useState, useEffect, useRef, useCallback } from "react"

interface UseTranscriptionPollingOptions {
  transcriptionJobId: string | null
  interval?: number
  onComplete?: () => void
}

function getCSRFToken(): string {
  const meta = document.querySelector('meta[name="csrf-token"]')
  return meta?.getAttribute("content") || ""
}

export function useTranscriptionPolling({
  transcriptionJobId,
  interval = 10000,
  onComplete,
}: UseTranscriptionPollingOptions) {
  const [isTranscribing, setIsTranscribing] = useState(!!transcriptionJobId)
  const onCompleteRef = useRef(onComplete)
  onCompleteRef.current = onComplete

  const poll = useCallback(async (jobId: string) => {
    try {
      const res = await fetch(`/api/transcriptions/${jobId}`, {
        headers: { "X-CSRF-Token": getCSRFToken() },
      })
      if (!res.ok) return
      const data = await res.json()
      const status = data.transcription_job?.status
      if (status === "complete" || status === "failed") {
        setIsTranscribing(false)
        if (status === "complete") {
          onCompleteRef.current?.()
        }
        return true // done polling
      }
    } catch {
      // keep polling on network errors
    }
    return false
  }, [])

  useEffect(() => {
    if (!transcriptionJobId) {
      setIsTranscribing(false)
      return
    }

    setIsTranscribing(true)
    let stopped = false
    let timer: ReturnType<typeof setTimeout>

    async function tick() {
      if (stopped) return
      const done = await poll(transcriptionJobId!)
      if (!stopped && !done) {
        timer = setTimeout(tick, interval)
      }
    }

    // Start first poll after a short delay
    timer = setTimeout(tick, 2000)

    return () => {
      stopped = true
      clearTimeout(timer)
    }
  }, [transcriptionJobId, interval, poll])

  return { isTranscribing }
}
